<script lang="ts">
  import { page } from '$app/stores';
  import { onMount } from 'svelte';
  import Button from '$components/ui/button.svelte';
  import { hasRichTextContent } from '$components/rich-text.svelte';
  import { elevarAnalyticsEvent, ElevarAnalyticsEvents } from '$lib/analytics';
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import { isIos } from '$lib/utils/device';
  import Seo from '$components/seo.svelte';
  import Gallery from '$components/pdp/gallery.svelte';
  import Alert from '$components/pdp/alert.svelte';
  import NameAndPrice from '$components/pdp/name-and-price.svelte';
  import Details from '$components/pdp/details.svelte';
  import Variants from '$components/pdp/variants/variants.svelte';
  import VariantsInformation from '$components/pdp/variants-information.svelte';
  import AddToCart from '$components/pdp/add-to-cart.svelte';
  import DynamicBlock from '$components/blocks/dynamic-block.svelte';
  import { intersectionObserver } from '$lib/actions/intersection-observer';
  import { drawer } from '$lib/stores/drawer';
  import { CART_DRAWER_ID, DEFAULT_TITLE, DEFAULT_VENDOR, GUEST } from '$lib/constants';

  import type { ProductDetailPage } from './content';
  import { setPDPProductContext } from '$lib/contexts/pdp-product';
  import { recentlyViewed } from '$lib/recently-viewed';
  import { AnalyticsEvents, analyticsEvent, klavyioViewedItem } from '$lib/analytics';
  import { trimId, trimVariantId } from '$lib/utils/shopify/productsId';
  import StyledByYouSection from '$components/blocks/styled-by-you-section.svelte';
  import { t } from '$lib/i18n';
  import { nostoSession, sendNostoEvent } from '$lib/nosto';
  import { modifyString } from '$lib/utils/strings';
  import { region } from '$lib/region';
  import { afterNavigate, goto, onNavigate } from '$app/navigation';
  import { browser } from '$app/environment';
  import { nostoRecommendations } from '$lib/nosto';
  import { createQuery } from '@tanstack/svelte-query';
  import { shopify } from '$lib/shopify';
  import { lang } from '$lib/lang';
  import type { LanguageCode } from '$gql/storefront/schemas';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';
  import BottomSection from '$components/blocks/bottom-section.svelte';
  import {
    categoryCampaigns,
    categoryCampaignPdpExclude,
    tieredCampaign,
    tieredCampaignPdpExclude,
    tieredCampaignColors,
    getCampaignColors,
    freeGiftCampaign
  } from '$lib/stores/campaigns';
  import { get } from 'svelte/store';
  import PromotionalBlock from '$components/promotional-block.svelte';
  import ProductImages from '$components/pdp/product-images.svelte';
  import type { GetProductQuery } from '$gql/storefront/operations';
  import { cn } from '$lib/utils';
  import Drawer from '$components/drawer.svelte';
  import ShopifyVariants from '$components/pdp/variants/shopify-variants.svelte';
  import { splitTitleAndCategory } from '$lib/utils/shopify/splitTitleAndCategory';
  import { ProductMetafields } from '$lib/constants';
  import ColorSelector from '$components/color-selector.svelte';
  import { base } from '$app/paths';
  import { getFullURL } from '$lib/utils/paths';
  import { getProductCategory } from '$lib/cart/cart';
  import AlertCard from '$components/alert-card.svelte';
  import { abTestVersion } from '$lib/stores/ab-test-version';
  import ShopifyPlans from '$components/pdp/variants/shopify-plans.svelte';
  import ProductBadges from '$components/ui/product-badges.svelte';
  import { isFittingBoxEnabled, startVirtualTryOn } from '$lib/utils/fittingbox';
  import SizeTables from '$components/pdp/size-tables.svelte';
  import CmsImage from '$components/cms-image.svelte';

  /* GraphQL */ `
    query getProduct($handle: String!, $language: LanguageCode) @inContext(language: $language) {
      product(handle: $handle) {
        handle
        id
        title
        description
        seo {
          title
          description
        }
        productType
        handle
        availableForSale
        vendor
        collections(first: 100) {
          nodes {
            title
          }
        }
        options {
          name
          values
        }
        images(first: 100) {
          nodes {
            url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
            altText
            width
            height
            id
            __typename
          }
        }
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
            currentlyNotInStock
            quantityAvailable
            sku
            barcode
            price {
              ...Money
            }
            compareAtPrice {
              ...Money
            }
            sellingPlanAllocations(first: 10) {
              edges {
                node {
                  sellingPlan {
                    id
                    name
                  }
                  priceAdjustments {
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
        priceRange {
          minVariantPrice {
            ...Money
          }
        }
        metafields(
          identifiers: [
            { namespace: "custom", key: "variantcolor" }
            { namespace: "custom", key: "save_on_sets" }
            { namespace: "custom", key: "multiple_variant_images_by_alt_text" }
            { namespace: "custom", key: "wishlist_counter_eu" }
            { namespace: "custom", key: "wishlist_counter_us" }
            { namespace: "custom", key: "wishlist_counter_uk" }
            { namespace: "custom", key: "a_test_version_media_index" }
            { namespace: "wishlist", key: "wishlist_counter" }
            { namespace: "custom", key: "pre_order_day" }
          ]
        ) {
          id
          key
          value
        }
        seo {
          title
          description
        }
        totalInventory
        tags
        media(first: 100) {
          nodes {
            previewImage {
              url(transform: { preferredContentType: WEBP, maxWidth: 1024 })
              id
            }
            __typename
            ... on Video {
              sources {
                url
              }
              alt
              id
            }
          }
        }
        sellingPlanGroups(first: 1) {
          edges {
            node {
              name
              sellingPlans(first: 10) {
                edges {
                  node {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      }
    }

    query getRegionalizedProductPrice($handle: String!, $lang: LanguageCode, $country: CountryCode)
    @inContext(language: $lang, country: $country) {
      product(handle: $handle) {
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
            currentlyNotInStock
            quantityAvailable
            sku
            price {
              ...Money
            }
            compareAtPrice {
              ...Money
            }
            sellingPlanAllocations(first: 10) {
              edges {
                node {
                  sellingPlan {
                    id
                    name
                  }
                  priceAdjustments {
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
        priceRange {
          minVariantPrice {
            ...Money
          }
        }
      }
    }

    query getRegionalizedProductPriceById($id: ID!, $lang: LanguageCode, $country: CountryCode)
    @inContext(language: $lang, country: $country) {
      product(id: $id) {
        id
        title
        handle
        productType
        variants(first: 100) {
          nodes {
            id
            title
            availableForSale
            currentlyNotInStock
            quantityAvailable
            sku
            price {
              ...Money
            }
            compareAtPrice {
              ...Money
            }
            sellingPlanAllocations(first: 10) {
              edges {
                node {
                  sellingPlan {
                    id
                    name
                  }
                  priceAdjustments {
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
        priceRange {
          minVariantPrice {
            ...Money
          }
        }
      }
    }

    query Localization @inContext(language: EN) {
      localization {
        availableLanguages {
          isoCode
          endonymName
        }
      }
    }
  `;

  $: regionalizedProduct = createQuery({
    staleTime: 2 * 60 * 1000,
    queryKey: ['regionalizedProduct', data.product.handle],
    queryFn: async () => {
      return await $shopify.getRegionalizedProductPrice({
        handle: data.product.handle,
        lang: $lang.toUpperCase() as LanguageCode,
        country: parseCountryAsCode($optimalUSCountry)
      });
    }
  });

  export let data: ProductDetailPage;

  $: selectedOutlineCallout = $page.data.configuration.content.outline_callout_titles?.find(
    (callout) => {
      const tags = (callout.tags || '').split('\n');
      return tags?.some((tag) => product.tags.includes(tag));
    }
  );
  $: showOutline = selectedOutlineCallout != null && selectedOutlineCallout?.pdp_image != '';
  $: outlineBackgroudCampaignColors =
    showOutline && selectedOutlineCallout?.colors
      ? getCampaignColors(selectedOutlineCallout.colors)
      : null;

  const product = data.product;
  const options = product.options;
  const variants = product.variants.nodes;

  const sellingPlans =
    product.sellingPlanGroups.edges.length > 0
      ? product.sellingPlanGroups.edges[0]?.node.sellingPlans.edges.map((e) => e.node)
      : [];

  $: selectedPlanId = sellingPlans.length > 0 ? sellingPlans[0]?.id || '' : undefined;

  const wishlistCounter = product.metafields?.find((m) => m?.key === `wishlist_counter`)?.value;

  const regionWishlistCounter =
    product.metafields?.find((m) => m?.key === `wishlist_counter_${$region}`)?.value || 0;

  /**
   * Handles region-specific wishlist counters (HEY-316)
   *
   * Background:
   * - Previously, all regions shared a single 'wishlist_counter' metafield
   * - With that, we could not know the wishlist counter for each region
   *
   * Solution:
   * 1. Created region-specific counters using format: `wishlist_counter_${region}`
   * 2. Original 'wishlist_counter' serves as base value
   * 3. Region-specific counter is added to the base value
   */
  const totalWishlistCounter = Number(wishlistCounter || 0) + Number(regionWishlistCounter || 0);

  const storyContent = data.story.content;
  const wontBeRestocked = !!storyContent.will_not_be_restocked;

  const hasOnlyOneVariantForSaleStock =
    variants?.filter((v) => v.availableForSale).length === 1
      ? product.totalInventory?.toString()
      : false;

  const resetSelected = () => {
    const result: Record<string, string> = {};

    if (variants.length > 1) {
      // Checks the index of the first variant that is available for sale
      const variantAvailable = variants.find((variant) => variant.availableForSale);
      const availableOptions = variantAvailable?.title.split(' / ') || [];

      options.forEach((option, i) => {
        if (variantAvailable) {
          // select the option indexed by the available options if option is more than one
          result[option.name] = options.length > 1 ? availableOptions[i] : variantAvailable?.title;
        } else {
          //  selects the first option if all variants are out of stock
          result[option.name] = option.values[0];
        }
      });
    } else {
      //  selects the first option if variant is just one
      options.forEach((option) => {
        result[option.name] = option.values[0];
      });
    }

    return result;
  };

  let selectedOptions: Record<string, string> = {};

  let showSecondAddToCart: boolean | undefined = undefined;
  const variantsInformationDrawerId = 'variants-information' + product.id;

  $: selectedVariant = Object.values(selectedOptions).join(' / ');

  $: selectedProductVariant = variants?.find((variant) => variant.title === selectedVariant);

  /* We need to make sure url params are updated when the user selects a variant
   * and not when the page loads for the first time. This prevents users having to go back
   * two times to get to the previous page.
   */
  let isTainted: boolean;
  $: if (!isTainted && !isFirstVariant && browser) {
    isTainted = true;
  }

  $: isFirstVariant = variants?.length > 1 ? selectedProductVariant === variants[0] : false;

  let screenSize = 0;

  let previousPage: string = base;
  afterNavigate(({ from }) => {
    previousPage = from?.url.pathname || previousPage;
  });

  onNavigate(({ to }) => {
    if ($drawer.id == CART_DRAWER_ID) {
      drawer.close();
    }

    let getVariantParamsRes = null;
    if (to) {
      getVariantParamsRes = getVariantParams(to?.url.searchParams);
    }

    if (getVariantParamsRes) {
      selectedOptions = getVariantParamsRes;
    }
  });

  $: if (
    selectedProductVariant &&
    variants.length > 1 &&
    isTainted &&
    $page.url.searchParams.get('variant') !== trimVariantId(selectedProductVariant.id)
    // The next line is to avoid a redirect when a user access the "master" product link. That
    // redirect is a problem when it comes to SEO.
    //TODO: reavaluate in 4 weeks
    // $page.url.searchParams.get('variant') &&
  ) {
    $page.url.searchParams.set('variant', trimVariantId(selectedProductVariant.id)),
      goto($page.url, { noScroll: previousPage == $page.url.pathname, replaceState: true });
  }

  const triggerElevar = () => {
    if (!selectedProductVariant) return;

    let collectionUrl = `/collections/${getProductCategory(product.title, product.productType) ?? ''}`;

    const variantTitle =
      selectedProductVariant.title === DEFAULT_TITLE || selectedProductVariant.title === 'Title'
        ? product.title
        : selectedProductVariant.title;

    const newPrice = compareAtPrice?.amount || product.priceRange?.minVariantPrice?.amount;
    const productPrice = hasCategoryCampaign
      ? categoryCampaign?.getDiscountedPrice(+newPrice)
      : newPrice;

    elevarAnalyticsEvent({
      event: ElevarAnalyticsEvents.VIEW_ITEM,
      user_properties: { visitor_type: GUEST },
      ecommerce: {
        currencyCode: product.priceRange?.minVariantPrice?.currencyCode,
        detail: {
          actionField: { list: collectionUrl, action: 'detail' },
          products: [
            {
              id: selectedProductVariant.sku,
              name: product.title,
              brand: product.vendor || DEFAULT_VENDOR,
              category: product.productType,
              variant: variantTitle,
              price: `${productPrice}`,
              list: collectionUrl,
              product_id: trimId(product.id),
              variant_id: trimVariantId(selectedProductVariant.id),
              compare_at_price: `${selectedProductVariant.compareAtPrice}` || '0.0',
              image: (product.images?.nodes || [])[0]?.url,
              url: getFullURL({
                path: `/products/${product.handle}`,
                region: $region,
                lang: $lang,
                searchQuery: $page.url.search || null
              })
            }
          ]
        }
      }
    });
  };

  $: if (selectedProductVariant) {
    // trigger elevar on variant selection
    triggerElevar();
  }

  /**
   * This returns an object with any variant params from the URL.
   * It uses product's options to return only the params that are valid. This means that if
   * the URL has a param that doesn't match any of the product's options, it will
   * be ignored.
   * @returns {Record<string, string>} - Object with the variant params from the URL
   *
   */

  $: getVariantParams = (searchParams: URLSearchParams) => {
    const variantParam = searchParams.get('variant');
    const options = product.options.map((o) => o.name);

    if (variantParam) {
      const splitVariantParam = variantParam.split('-');
      const matchingVariant = variants?.find(
        (v) => v.id === `gid://shopify/ProductVariant/${splitVariantParam[0]}`
      );
      const matchingVariantSplit = matchingVariant?.title.split(' / ') || [];

      if (splitVariantParam.length) {
        const result: Record<string, string> = {};

        options.forEach((option, i) => {
          result[option] =
            options.length > 1 ? matchingVariantSplit[i] : matchingVariant?.title || '';
        });

        return result;
      }
    }

    return (
      options?.reduce<Record<string, string>>((acc, option) => {
        const possibleCombinations = Object.values(modifyString(option || ''));
        const searchParams = possibleCombinations.find((s) => $page.url.searchParams.get(s)) || '';
        const optionParam = $page.url.searchParams.get(searchParams)?.replaceAll('-', ' ');
        const optionParamLowerCase = $page.url.searchParams
          .get(option.toLowerCase())
          ?.replaceAll('-', ' ');

        acc[option] = optionParam || optionParamLowerCase || '';
        return acc;
      }, {}) || {}
    );
  };

  $: variantParams = getVariantParams($page.url.searchParams);
  $: hasVariantParams = Object.values(variantParams).some((v) => !!v);

  $: hasSizeGuide =
    storyContent.variants_information && hasRichTextContent(storyContent.variants_information);

  // Ring Sizer
  $: ringSizerOnRingPagesBlock = $page.data.configuration.content.ring_sizer_on_ring_pages_link;
  $: ringSizerOnRingPagesLink = ringSizerOnRingPagesBlock
    ? $getAnchorFromCmsLink(ringSizerOnRingPagesBlock)
    : null;
  $: isRingSizerOnAllDevices = $page.data.configuration.content.is_ring_sizer_on_all_devices;

  $: isRingPage =
    splitTitleAndCategory({
      title: product.title,
      titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
    }).category == 'Ring';

  $: showFittingBox =
    splitTitleAndCategory({
      title: product.title,
      titlesCategoriesExceptions: $page.data.titlesCategoriesExceptions
    }).category == 'Sunglasses' && isFittingBoxEnabled();

  /**
   * Some blocks might need to use the product data when rendered within the PDP,
   * so need to create a context exposing some useful information
   */
  setPDPProductContext(data.product);

  onMount(() => {
    recentlyViewed.add(data.handle);

    if (get(tieredCampaign)?.isProductExcluded(product)) {
      tieredCampaignPdpExclude.set(true);
    }
    if (categoryCampaign?.isProductExcluded(product)) {
      categoryCampaignPdpExclude.set(true);
    }

    // If the URL has variant params, we need to update the selectedOptions
    if (hasVariantParams) {
      selectedOptions = variantParams;
    } else {
      selectedOptions = resetSelected();
    }

    const productVariant = variants.find(
      (variant) => variant.title === Object.values(selectedOptions).join(' / ')
    );
    const variantTitle =
      productVariant?.title === DEFAULT_TITLE || productVariant?.title === 'Title'
        ? product.title
        : productVariant?.title;

    analyticsEvent({
      event: AnalyticsEvents.VIEW_ITEM,
      items: [
        {
          item_id: product.handle,
          item_name: product.title,
          item_category: product.productType,
          item_variant: variantTitle || '',
          item_sku: productVariant?.sku || ''
        }
      ],
      test_group: $abTestVersion
    });

    klavyioViewedItem({
      ItemId: trimId(product.id),
      Title: product.title,
      Categories: product.collections.nodes.map((n) => n.title),
      ImageUrl: product.images.nodes[0]?.url || '',
      URL: $page.url.pathname,
      Metadata: {
        Brand: product.vendor,
        Price: Number(product.variants.nodes[0].price.amount),
        CompareAtPrice: product.variants.nodes[0].compareAtPrice?.amount
          ? Number(product.variants.nodes[0].compareAtPrice?.amount)
          : null
      }
    });

    return () => {
      categoryCampaignPdpExclude.set(false);
      tieredCampaignPdpExclude.set(false);
    };
  });

  $: sessionToken = $nostoSession;
  $: if (sessionToken) {
    sendNostoEvent({
      type: 'sendProducts',
      nostoSession: sessionToken,
      target: trimId(product.id),
      region: $region,
      slotId: data.story?.content?.nosto_slot_id
    }).then((res) => {
      nostoRecommendations.set(res.data.updateSession.pages.forProductPage[0].primary);
    });
  }

  $: regionalizedProductSelectedVariant =
    $regionalizedProduct.data?.product?.variants.nodes?.filter(
      (v) => v.id === selectedProductVariant?.id
    )[0];

  $: compareAtPrice =
    regionalizedProductSelectedVariant?.compareAtPrice || selectedProductVariant?.compareAtPrice;

  $: currency =
    $regionalizedProduct.data?.product?.priceRange?.minVariantPrice?.currencyCode ||
    product.priceRange?.minVariantPrice?.currencyCode ||
    'USD';

  $: hasTieredCampaign = !!$tieredCampaign && !$tieredCampaign.isProductExcluded(product);
  $: hasCategoryCampaign =
    !!$categoryCampaigns &&
    $categoryCampaigns.some((campaign) => !campaign.isProductExcluded(product)) &&
    !hasTieredCampaign;
  $: categoryCampaign = $categoryCampaigns?.find(
    (campaign) => !campaign.isProductExcluded(product)
  );

  $: if (categoryCampaign) {
    document.body.setAttribute('data-category-color', categoryCampaign.color || '1');
  }

  let tieredAltTextColor: string | undefined = undefined;
  let tieredAltBackgroundColor: string | undefined = undefined;

  let categoryAltTextColor: string | undefined = undefined;
  let categoryAltBackgroundColor: string | undefined = undefined;

  $: tieredAltTextColor = $tieredCampaignColors.tieredAltTextColor;
  $: tieredAltBackgroundColor = $tieredCampaignColors.tieredAltBackgroundColor;

  $: if (categoryCampaign?.campaign_color) {
    const categoryCampaignColors = getCampaignColors(categoryCampaign.campaign_color);
    categoryAltTextColor = categoryCampaignColors.altTextColor;
    categoryAltBackgroundColor = categoryCampaignColors.altBackgroundColor;
  }

  const parseColorSelectorMetafield = (product: NonNullable<GetProductQuery['product']>) => {
    const metafield = product.metafields.find((p) => p?.key === ProductMetafields.VARIANT_COLOR);
    if (
      product.metafields.find((p) => p?.key === ProductMetafields.VARIANT_IMAGES) &&
      metafield &&
      Array.isArray(JSON.parse(metafield.value)) &&
      !!JSON.parse(metafield.value).length
    ) {
      return metafield;
    }
  };

  $: variantColor = parseColorSelectorMetafield(product);

  $: todaysFreeGifts = $freeGiftCampaign?.campaign_free_gifts
    ?.filter((p) => typeof p !== 'string')
    .map((p) => p.slug);

  $: isFreeGift =
    (todaysFreeGifts || []).includes(product.handle) &&
    $freeGiftCampaign?.free_gifts_cannot_be_bought;

  // Pre-Order
  $: preOrderDay = product.metafields.find(
    (p) => p?.key === ProductMetafields.PRE_ORDER_DAY
  )?.value;
  $: isPreSale = !!preOrderDay;
</script>

<svelte:window bind:innerWidth={screenSize} />
<Seo
  title={storyContent.seo_title || product.seo.title}
  description={storyContent.seo_description || product.seo.description}
  image={storyContent.seo_og_image}
/>
<div
  class="relative [&>:last-child]:pb-5 lg:[&>:last-child]:pb-8"
  style:--outlineHeaderHeight={showOutline ? '20px' : '0px'}
  style:--tieredAltBackgroundColor={tieredAltBackgroundColor}
  style:--tieredAltTextColor={tieredAltTextColor}
  style:--categoryAltBackgroundColor={categoryAltBackgroundColor}
  style:--categoryAltTextColor={categoryAltTextColor}
  style:--outlineBackgroudColor={outlineBackgroudCampaignColors?.altTextColor}
>
  <section class="p-2 lg:p-8">
    <div class="container relative mx-auto lg:grid lg:grid-cols-[1fr_432px] lg:gap-8">
      <ProductBadges
        {product}
        stylesClass="absolute z-10 top-[calc(10px+var(--outlineHeaderHeight))] left-2.5 max-w-[432px]"
      />

      <ProductImages
        {product}
        {selectedOptions}
        {showOutline}
        outlineTitle={selectedOutlineCallout?.pdp_image}
        isBlackOutlineColor={outlineBackgroudCampaignColors?.isBlackColor}
        imageDescription={storyContent.image_information}
      >
        {#if showFittingBox}
          <Button
            size="sm"
            icon="glasses"
            variant="blur"
            class="absolute top-[calc(10px+var(--outlineHeaderHeight))] right-2.5 z-10 flex flex-row w-fit"
            on:click={() => startVirtualTryOn(selectedProductVariant?.barcode)}
          >
            <span>{$t('glasses.try-on')}</span>
          </Button>
        {/if}
      </ProductImages>

      <!-- Mobile -->
      <div
        class={cn(
          'lg:hidden',
          showOutline &&
            (outlineBackgroudCampaignColors?.isBlackColor
              ? 'border border-accent bg-accent rounded-md'
              : 'border border-[--outlineBackgroudColor] bg-[--outlineBackgroudColor] rounded-md')
        )}
      >
        {#if showOutline}
          <p class="py-1 px-1 text-center text-highlight text-xs font-medium">
            {selectedOutlineCallout?.pdp_image}
          </p>
        {/if}
        <Gallery
          {product}
          {selectedOptions}
          showVideoButton={true}
          imageDescription={storyContent.image_information}
          class="lg:hidden bg-background"
        />
      </div>

      {#if showFittingBox}
        <Button
          size="sm"
          icon="glasses"
          variant="blur"
          class="absolute top-[calc(10px+var(--outlineHeaderHeight))] right-2.5 z-10 flex flex-row w-fit lg:hidden"
          on:click={() => startVirtualTryOn(selectedProductVariant?.barcode)}
        >
          <span>Try-on</span>
        </Button>
      {/if}
      <!-- Desktop -->
      <div
        class="lg:sticky lg:top-[116px] lg:h-fit lg:max-h-full lg:self-start lg:overflow-y-auto hide-scrollbar"
      >
        {#if data.isAdminProduct}
          <AlertCard
            class="mb-4 mt-2 lg:mt-0 bg-red text-white"
            message={`This product is only visible in STAGING`}
          />
        {:else}
          <Alert
            {product}
            {isPreSale}
            {wontBeRestocked}
            wishlistCounter={`${totalWishlistCounter}`}
            {hasOnlyOneVariantForSaleStock}
          />
        {/if}

        <div class="mt-4 px-2 lg:mt-0 lg:px-0">
          <NameAndPrice {product} class="mb-6" />

          {#if variantColor?.value && selectedVariant && !!options?.length}
            <ColorSelector
              bind:selectedOptions
              metafield={variantColor.value}
              selectorVariant="pdp"
              class="mb-4"
            />
          {:else}
            <Variants
              bind:selectedOptions
              {product}
              {isPreSale}
              {wontBeRestocked}
              variantsAsProducts={storyContent.variants_as_products}
              sizeGuideOnClick={hasSizeGuide && !storyContent.show_variant_info_in_place
                ? () => {
                    drawer.open(variantsInformationDrawerId);
                  }
                : undefined}
            />
          {/if}

          {#if sellingPlans.length > 0 && selectedPlanId}
            <ShopifyPlans {product} bind:selectedPlanId />
          {/if}

          {#if hasSizeGuide}
            {#if !storyContent.show_variant_info_in_place}
              <Drawer
                id={variantsInformationDrawerId}
                title={storyContent.variant_info_drawer_title || $t('pdp.variants.drawer-title')}
              >
                <div class="mx-2 lg:mx-4 mt-6">
                  {#if storyContent?.synced_size_table?.content?.size_tables && storyContent?.synced_size_table?.content?.size_tables?.length > 0}
                    <SizeTables block={storyContent?.synced_size_table?.content?.size_tables} />
                    {#if storyContent?.synced_size_table?.content?.size_tables_image}
                      <CmsImage
                        image={storyContent?.synced_size_table?.content?.size_tables_image}
                        class="mt-4 w-full rounded-md border border-primary/4 object-contain'"
                      />
                    {/if}
                  {:else}
                    <div class="flex flex-col gap-6 mb-4">
                      <ShopifyVariants
                        {product}
                        bind:selectedOptions
                        showStockInfo={false}
                        isPreSale={true}
                        wontBeRestocked={true}
                      />
                    </div>

                    <VariantsInformation
                      {selectedProductVariant}
                      doc={storyContent.variants_information}
                      class="mb-3 lg:mb-5"
                    />
                  {/if}
                </div>
              </Drawer>
            {:else if storyContent?.synced_size_table?.content && storyContent?.synced_size_table?.content?.size_tables && storyContent?.synced_size_table?.content?.size_tables?.length > 0}
              <SizeTables block={storyContent?.synced_size_table?.content?.size_tables} />
              {#if storyContent?.synced_size_table?.content?.size_tables_image}
                <CmsImage
                  image={storyContent?.synced_size_table?.content?.size_tables_image}
                  class="mt-4 w-full rounded-md border border-primary/4 object-contain'"
                />
              {/if}
            {:else}
              <VariantsInformation
                {selectedProductVariant}
                doc={storyContent.variants_information}
                class="mb-3 lg:mb-5"
              />
            {/if}
          {/if}

          {#if ringSizerOnRingPagesLink && isRingPage}
            <Button
              as="a"
              size="sm"
              variant="ghost"
              class={cn('mb-3 lg:mb-5', !isRingSizerOnAllDevices && !isIos() && 'hidden')}
              href={ringSizerOnRingPagesLink?.href}
              rel={ringSizerOnRingPagesLink?.rel}
              targer={ringSizerOnRingPagesLink?.target}>{$t('pdp.ring-sizer.button.title')}</Button
            >
          {/if}

          {#if isFreeGift && $freeGiftCampaign && $freeGiftCampaign.promotional_blocks}
            <div class="lg:col-span-4 col-span-2 flex gap-2 [&>*]:flex-1 lg:mt-5">
              {#each $freeGiftCampaign.promotional_blocks as block}
                <PromotionalBlock {block} variant="sm" color={$freeGiftCampaign?.campaign_color} />
              {/each}
            </div>
          {/if}

          <div
            use:intersectionObserver={{
              callback: ([e]) => {
                showSecondAddToCart = !e.isIntersecting && !(e.boundingClientRect.y > 0);
              },
              options: { threshold: [0] }
            }}
          >
            <AddToCart
              {product}
              {isPreSale}
              {currency}
              {compareAtPrice}
              {selectedPlanId}
              {isFreeGift}
              selectedProductVariant={regionalizedProductSelectedVariant}
              hideKlarna={sellingPlans.length > 0}
            />
          </div>

          <AddToCart
            class={cn(
              'fixed transition-all duration-300 left-0 right-0 z-[20] h-fit px-4 lg:hidden translate-y-0 py-0',
              get(tieredCampaign) ? 'bottom-[42px]' : 'bottom-4',
              !showSecondAddToCart && 'translate-y-20'
            )}
            {product}
            {isPreSale}
            {currency}
            {compareAtPrice}
            {selectedPlanId}
            {isFreeGift}
            selectedProductVariant={regionalizedProductSelectedVariant}
            showJustAddCardButton={true}
            hideKlarna={sellingPlans.length > 0}
          />

          {#if $tieredCampaign && hasTieredCampaign && $tieredCampaign.showBannersOnPDP && !!$tieredCampaign.promotional_blocks.length && !isFreeGift}
            <div class="lg:col-span-4 col-span-2 flex gap-2 [&>*]:flex-1 lg:mt-5">
              {#each $tieredCampaign.promotional_blocks as block}
                <PromotionalBlock {block} variant="sm" color={$tieredCampaign?.campaign_color} />
              {/each}
            </div>
          {/if}

          {#if !hasTieredCampaign && categoryCampaign && hasCategoryCampaign && categoryCampaign.showBannersOnPDP && !!categoryCampaign.promotional_blocks.length && !isFreeGift}
            <div class="lg:col-span-4 col-span-2 flex gap-2 [&>*]:flex-1 lg:mt-5">
              {#each categoryCampaign.promotional_blocks as block}
                <PromotionalBlock {block} variant="sm" color={categoryCampaign?.campaign_color} />
              {/each}
            </div>
          {/if}

          <Details
            {product}
            {selectedOptions}
            description={storyContent.description}
            details={storyContent.product_description}
            additionalInformation={storyContent.additional_information}
          />
        </div>
      </div>
    </div>
  </section>

  <!-- TODO: Re add sets with a client-side approach. Eventually we can prefetch queries SSR -->
  <!-- {#if data.setsWithProduct.length > 0}
    <section class="px-2 py-12 lg:px-8 lg:py-16">
      <div class="container mx-auto">
        <h3 class="mb-4 px-2 text-4xl font-semibold lg:px-0">{$t('pdp.shop-the-set')}</h3>
        <ProductGrid products={data.setsWithProduct} />
      </div>
    </section>
  {/if} -->

  <!-- If the editors didn't include a styled by you block -->
  {#if !data.story.content.body || !data.story.content.body.some((b) => b.component === 'styled-by-you-section')}
    <StyledByYouSection
      block={{
        _uid: `styled-by-you-${data.handle}`,
        component: 'styled-by-you-section',
        title: [
          {
            title: $t('styled-by-you.title'),
            variant: 'left',
            component: 'section-title',
            _uid: `styled-by-you-title-${data.handle}`
          }
        ]
      }}
    />
  {/if}

  <!-- If the editors didn't include a recommendations block, we'll add a fake one just to guarantee we have something -->
  {#if !data.story.content.body || (!data.story.content.body.some((b) => b.component === 'recommendations') && !data.story.content.body.some((b) => b.component === 'ab-test-content' && b.body?.some((c) => c.component === 'recommendations')) && !data.story.content.body.some((b) => b.component === 'bottom-section' && b.content.some((c) => c.component === 'product-recommendations')))}
    <BottomSection
      block={{
        _uid: `fake-recommendations-${data.handle}`,
        component: 'bottom-section',
        title: $t('pdp.recommendations.you-may-also-like'),
        content: [
          {
            _uid: `fake-recommendations-content-${data.handle}`,
            automatic_recommendations: 'RELATED',
            component: 'product-recommendations'
          }
        ]
      }}
    />
  {/if}

  {#if data.story.content.body}
    {#each data.story.content.body as b}
      <DynamicBlock block={b} />
    {/each}
  {/if}
</div>
